import { ThemedButton } from '@/app/platform/core/layout/themes/components/ThemedButton.tsx';
import { ThemedSvgIcon } from '@/app/platform/core/layout/themes/icons/ThemedSvgIcon.tsx';
import { DocumentFormPagesEditor } from '@/app/platform/functions/documents/DocumentFormPagesEditor.tsx';
import { ThemedCard } from '@/app/platform/core/layout/themes/components/ThemedCard.tsx';
import { ThemedLoadingOverlay } from '@/app/platform/core/layout/themes/components/ThemedLoadingOverlay.tsx';
import { ThemedLoadingSpinner } from '@/app/platform/core/layout/themes/components/ThemedLoadingSpinner.tsx';
import { ProjectDocumentFields } from '@/app/platform/functions/documents/ProjectDocumentFields.tsx';
import { AppLayout } from '@/app/platform/core/layout/AppLayout.tsx';
import { ThemeProvider } from '@/app/platform/core/layout/themes/ThemeContext.tsx';
import { useDocumentPages } from '@/app/platform/functions/documents/useDocumentPages.ts';
import { useProjectDocument } from '@/app/features/tribes-projects/projects/useProjectDocuments.ts';
import { useProjectWithDocument } from '@/app/features/tribes-projects/projects/useProjects.ts';
import { useTribeWithPositions } from '@/app/features/tribes-projects/tribes/useTribesWithPositions.ts';
import { labelService } from '@/app/platform/functions/labels/label.service.ts';
import { projectDocumentService } from '@/app/features/tribes-projects/projects/project-document.service.ts';
import {
  errorStyle,
  formActionsStyle,
  formContainerStyle,
} from '@/app/platform/core/layout/themes/theme.styles.tsx';
import { AttachmentFile } from '@/app/platform/functions/documents/document.types.ts';
import { MenuAction } from '@/app/platform/core/layout/menu.types.ts';

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

const ProjectDocumentFormPageContent: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tribeId, projectId, projectDocumentId } = useParams<{
    tribeId: string;
    projectId: string;
    projectDocumentId?: string;
  }>();

  const isEdit = !!projectDocumentId;

  const { tribe } = useTribeWithPositions(tribeId || null);
  const { project } = useProjectWithDocument(projectId || null);
  const { document: existingDoc, loading: loadingDoc } = useProjectDocument(
    isEdit ? projectId || null : null,
    isEdit ? projectDocumentId || null : null,
  );
  const {
    pages,
    loading: pagesLoading,
    refetch: refetchPages,
  } = useDocumentPages(
    isEdit ? projectId || null : null,
    isEdit ? projectDocumentId || null : null,
  );

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [labelNames, setLabelNames] = useState<string[]>([]);
  const [tocDepth, setTocDepth] = useState<number>(4);
  const [allLabelSuggestions, setAllLabelSuggestions] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    labelService
      .getAll()
      .then((labels) => {
        setAllLabelSuggestions(labels.map((l) => l.name));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (isEdit && existingDoc && !initialized) {
      setTitle(existingDoc.title);
      setContent(existingDoc.content_html);
      setAttachments(existingDoc.attachments);
      setLabelNames(existingDoc.labels.map((l) => l.name));
      setTocDepth(existingDoc.toc_depth ?? 4);
      setInitialized(true);
    }
    if (!isEdit && !initialized) {
      setInitialized(true);
    }
  }, [isEdit, existingDoc, initialized]);

  const breadcrumbs = [
    { label: t('common.home'), path: '/app' },
    { label: t('tribes.title'), path: '/app/tribes' },
    {
      label: tribe?.name || t('common.loading'),
      path: `/app/tribes/${tribeId}`,
    },
    {
      label: project?.name || t('common.loading'),
      path: `/app/tribes/${tribeId}/projects/${projectId}`,
    },
    {
      label: isEdit
        ? existingDoc?.title || t('common.loading')
        : t('projectDocuments.newDocument'),
    },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId) return;
    if (!title.trim()) {
      setError(t('projectDocuments.titleRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && projectDocumentId) {
        await projectDocumentService.update(projectId, projectDocumentId, {
          title: title.trim(),
          content_html: content,
          attachments,
          label_names: labelNames,
          toc_depth: tocDepth,
        });
        navigate(
          `/app/tribes/${tribeId}/projects/${projectId}/documents/${projectDocumentId}`,
        );
      } else {
        const created = await projectDocumentService.create(projectId, {
          title: title.trim(),
          content_html: content,
          attachments,
          label_names: labelNames,
          toc_depth: tocDepth,
        });
        navigate(
          `/app/tribes/${tribeId}/projects/${projectId}/documents/${created.url_param_id}`,
        );
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('validation.errorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  const cancelPath =
    isEdit && projectDocumentId
      ? `/app/tribes/${tribeId}/projects/${projectId}/documents/${projectDocumentId}`
      : `/app/tribes/${tribeId}/projects/${projectId}`;

  const menuActions = useMemo(
    (): MenuAction[] => [
      {
        id: 'projectDocument.cancel',
        icon: 'x',
        badgeIcon: 'file-text',
        label: t('common.cancel'),
        path: cancelPath,
      },
    ],
    [t, cancelPath],
  );

  if (isEdit && loadingDoc) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <div
          style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}
        >
          <ThemedLoadingSpinner size="sm" />
        </div>
      </AppLayout>
    );
  }

  if (isEdit && !loadingDoc && !existingDoc) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <ThemedCard>
          <div style={errorStyle}>
            <strong>{t('common.error')}</strong>{' '}
            {t('projectDocuments.notFound')}
          </div>
        </ThemedCard>
      </AppLayout>
    );
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs} menuActions={menuActions}>
      {submitting && (
        <ThemedLoadingOverlay
          message={
            isEdit
              ? t('projectDocuments.updating')
              : t('projectDocuments.creating')
          }
        />
      )}

      {error && (
        <div style={errorStyle}>
          <strong>{t('common.error')}</strong> {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={formContainerStyle}>
          <ProjectDocumentFields
            title={title}
            onTitleChange={setTitle}
            labelNames={labelNames}
            onLabelNamesChange={setLabelNames}
            allLabelSuggestions={allLabelSuggestions}
            tocDepth={tocDepth}
            onTocDepthChange={setTocDepth}
            content={content}
            onContentChange={setContent}
            attachments={attachments}
            onAttachmentsChange={setAttachments}
          />

          {isEdit && projectDocumentId && (
            <DocumentFormPagesEditor
              tribeId={tribeId!}
              projectId={projectId!}
              projectDocumentId={projectDocumentId}
              pages={pages}
              loading={pagesLoading}
              onReordered={refetchPages}
            />
          )}

          <div style={formActionsStyle}>
            <ThemedButton
              variant="secondary"
              onClick={() => navigate(cancelPath)}
              disabled={submitting}
              leftIcon={
                <ThemedSvgIcon name="x" color="currentColor" size={16} />
              }
            >
              {t('common.cancel')}
            </ThemedButton>
            <ThemedButton
              type="submit"
              variant="primary"
              isLoading={submitting}
              disabled={submitting || !title.trim()}
              leftIcon={
                <ThemedSvgIcon name="save" color="currentColor" size={16} />
              }
            >
              {isEdit ? t('common.update') : t('common.create')}
            </ThemedButton>
          </div>
        </div>
      </form>
    </AppLayout>
  );
};

export const ProjectDocumentFormPage: React.FC = () => (
  <ThemeProvider defaultTheme="default">
    <ProjectDocumentFormPageContent />
  </ThemeProvider>
);
